// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useContext, useEffect, useMemo, useState } from 'react';
import format from 'date-fns/format';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AchOutContext } from '../pages';
import Heading from '../../../generic-components/Heading';
import { TRANSFER_DONE_DATA } from '../constants';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import ReviewItemWrapper from './ReviewItemWrapper';
import Button from '../../../generic-components/Button';
import { refactoredTimeAndDate } from '../../../helpers/timeFormatter.helper';
import { DATE_FORMAT } from '../../../constants/general';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import { VIEWS_MAP } from '../../../router/constants';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { useStore } from '../../../store';
import { ReactComponent as IconVSuccessSign } from '../../../assets/icons/svg/colored-icons/ic-colored-success.svg';
import { ReactComponent as IconDownload } from '../../../assets/svg/shared/icon-download.svg';
import TransferSuccessPopup from './TransferSuccessPopup';
import { takeSnapshot } from '../../../services/snapshot.service';

interface IProps {
  makeAnotherTransfer: () => void;
}

const TransferDoneAchOut = ({ makeAnotherTransfer }: IProps) => {
  const navigate = useNavigate();
  const [isSnapshotMode, setIsSnapshotMode] = useState<boolean>(false);
  const chosenAchFee = useStore((state) => state.chosenAchFee);
  const { t } = useTranslation();
  const { HEADING, DISCLAIMER, CONTAINER, TRANSFER_DETAILS, MAKE_ANOTHER_CTA, DONE_CTA } = TRANSFER_DONE_DATA;
  const FlowContext = useContext(AchOutContext);
  const getBankTransferFormData = useStore((state) => state.getBankTransferFormData);
  const { businessName, transferAmount, accountNumber, routingNumber, feeData } = getBankTransferFormData();
  const { achOutResponse } = FlowContext;
  const contextType = useStore((state) => state.contextType);

  const { refetch: updateSummary, isLoading, isRefetching } = useAccountSummaryQuery();

  const shouldShowMakeAnotherTransfer = useMemo(() => {
    const restrictedContextTypes = ['BILL_PAY'];
    return !restrictedContextTypes.includes(contextType);
  }, [contextType]);

  const handleDone = () => {
    if (contextType === 'BILL_PAY') {
      navigate(`/${VIEWS_MAP.BILLS.BASE}`);
    } else {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
    }
  };
  const successContent = (
    <div className={CONTAINER.CSS}>
      <div className='bg-bw02 pt-[22px]'>
        <IconVSuccessSign className='m-auto w-[38px] h-[38px]' />
        <Heading testId='TRANSFER_DETAILS_TITLE' title={t(HEADING.TEXT)} className={HEADING.CSS} />
        <Heading testId='TRANSFER_DETAILS_DISCLAIMER' title={t(DISCLAIMER.TEXT)} className={DISCLAIMER.CSS} />
      </div>
      <ReviewItemWrapper>
        <div className={TRANSFER_DETAILS.STYLES.UPPER_ROW}>
          <Heading
            testId='TRANSFER_DETAILS_AMOUNT_PLACE_HOLDER'
            title={t(TRANSFER_DETAILS.AMOUNT.PLACE_HOLDER)}
            className='text-bw07 font-metricR text-md'
          />
          <div className='text-center'>
            <Heading
              testId='TRANSFER_DETAILS_AMOUNT'
              title={convertValueToUSD(Number(transferAmount), 2)}
              className={TRANSFER_DETAILS.AMOUNT.CSS}
            />
          </div>
        </div>
        <div className={TRANSFER_DETAILS.STYLES.ROW}>
          <Heading
            testId='TRANSFER_DETAILS_RECIPIENT_PLACE_HOLDER'
            title={t('ACH_OUT_SUCCESS.TO')}
            className={TRANSFER_DETAILS.STYLES.ROW_TITLE}
          />
          <div className={TRANSFER_DETAILS.STYLES.VALUE_WRAPPER}>
            <Heading
              testId='TRANSFER_DETAILS_RECIPIENT'
              title={businessName}
              className={TRANSFER_DETAILS.RECIPIENT.CSS}
            />
          </div>
        </div>
        <div className={TRANSFER_DETAILS.STYLES.ROW}>
          <Heading
            testId='TRANSFER_DETAILS_ROUTE_NUM_PLACE_HOLDER'
            title={t('ACH_OUT_SUCCESS.ROUTING_NUMBER')}
            className={TRANSFER_DETAILS.STYLES.ROW_TITLE}
          />
          <div className={TRANSFER_DETAILS.STYLES.VALUE_WRAPPER}>
            <Heading
              testId='TRANSFER_DETAILS_ROUTE_NUM'
              title={routingNumber}
              className={TRANSFER_DETAILS.STYLES.ROW_VALUE}
            />
          </div>
        </div>
        <div className={TRANSFER_DETAILS.STYLES.ROW}>
          <Heading
            testId='TRANSFER_DETAILS_ACCOUNT_NUM_PLACE_HOLDER'
            title={t('ACH_OUT_SUCCESS.ACCOUNT_NUMBER')}
            className={TRANSFER_DETAILS.STYLES.ROW_TITLE}
          />
          <div className={TRANSFER_DETAILS.STYLES.VALUE_WRAPPER}>
            <Heading
              testId='TRANSFER_DETAILS_ACCOUNT_NUM'
              title={accountNumber}
              className={TRANSFER_DETAILS.STYLES.ROW_VALUE}
            />
          </div>
        </div>
        <div className={TRANSFER_DETAILS.STYLES.ROW}>
          <Heading
            testId='TRANSFER_DETAILS_EST_DELIVERY_PLACE_HOLDER'
            title={t('ACH_OUT_SUCCESS.ESTIMATED_DELIVERY')}
            className={TRANSFER_DETAILS.STYLES.ROW_TITLE}
          />
          <div className={TRANSFER_DETAILS.STYLES.VALUE_WRAPPER}>
            <Heading
              testId='TRANSFER_DETAILS_EST_DELIVERY'
              title={feeData.arrivalDateShort}
              className={TRANSFER_DETAILS.STYLES.ROW_VALUE}
            />
          </div>
        </div>
        <div className={TRANSFER_DETAILS.STYLES.ROW}>
          <Heading
            testId='TRANSFER_DETAILS_FEE_PLACE_HOLDER'
            title={t('ACH_OUT_SUCCESS.FEE')}
            className={TRANSFER_DETAILS.STYLES.ROW_TITLE}
          />
          <div className={TRANSFER_DETAILS.STYLES.VALUE_WRAPPER}>
            <Heading
              testId='TRANSFER_DETAILS_FEE'
              title={convertValueToUSD(chosenAchFee.fee, 2)}
              className={TRANSFER_DETAILS.STYLES.ROW_VALUE}
            />
          </div>
        </div>
        <div className={TRANSFER_DETAILS.STYLES.LOWER_ROW}>
          <Heading
            testId='TRANSFER_DETAILS_TRAN_DATE'
            title={`${t('ACH_OUT_SUCCESS.DATE')} ${achOutResponse.processTime ? refactoredTimeAndDate(achOutResponse.processTime, 'ACH-out', DATE_FORMAT.LETTERS) : ''}`}
            className={TRANSFER_DETAILS.PROC_DATE.CSS}
          />
        </div>
      </ReviewItemWrapper>
    </div>
  );

  const downloadButtonContent = (
    <div className='flex '>
      <IconDownload />
      <div className='text-sm text-bw07 font-metricRSemibold ml-[5px]'>{t('ACH_OUT_SUCCESS.DOWNLOAD')}</div>
    </div>
  );
  const handleDownloadClick = () => {
    trackMixPanel('Act on ACH to 3rd Party - Success Screen', { Action: 'Download' });
    setIsSnapshotMode(true);
    setTimeout(() => {
      takeSnapshot('bankTransferSuccessPopup', true).then((url) => {
        const elLink = document.createElement('a');
        elLink.setAttribute('href', url as string);
        elLink.setAttribute('download', `bankTransfer-${format(new Date(), 'MMM dd, yyyy')}`);
        elLink.click();
        setIsSnapshotMode(false);
      });
    }, 150);
  };
  useEffect(() => {
    trackMixPanel('View ACH to 3rd Party - View Success Screen', {
      'Transfer Time': feeData.achTransferType === 'EXPRESS' ? 'Express' : 'Standard',
      Amount: Number(transferAmount),
      'Fee Paid': feeData.fee,
    });
    updateSummary();
  }, []);
  return (
    <div className='flex flex-col items-center justify-center w-full h-full'>
      <div className='w-full flex flex-col items-center h-full grow bg-bw02'>
        <div>
          {isSnapshotMode && <TransferSuccessPopup>{successContent}</TransferSuccessPopup>}
          {successContent}
        </div>
        <div className='flex justify-center mt-[16px] bg-bw00 rounded-[100px]'>
          <Button
            onClick={handleDownloadClick}
            data-testid='TRANSFER_DETAILS_DOWNLOAD_BUTTON'
            className='h-[40px] flex w-[151px] justify-center items-center border-bw05 rounded-[100px] border-[1px]'
            childElement={downloadButtonContent}
            id='bankTransferSuccessDownload'
          />
        </div>
      </div>
      <div className='w-full flex items-center justify-center py-[24px] border-t border-bw03 fadeIn'>
        {shouldShowMakeAnotherTransfer && (
          <Button
            isLoading={isLoading || isRefetching}
            disable={isLoading || isRefetching}
            testId='MakeAnotherTransferCTA'
            onClick={() => makeAnotherTransfer()}
            className={MAKE_ANOTHER_CTA.CSS}
            title={t('ACH_OUT_SUCCESS.MAKE_ANOTHER_TRANSFER')}
            id='MakeAnotherTransfer'
          />
        )}
        <Button
          isLoading={isLoading || isRefetching}
          disable={isLoading || isRefetching}
          testId='DoneCTA'
          onClick={handleDone}
          className={DONE_CTA.CSS}
          title={t('ACH_OUT_SUCCESS.DONE')}
          id='DoneCTA'
        />
      </div>
    </div>
  );
};

export default TransferDoneAchOut;
